import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'

import accounting from 'accounting'

// Shared
import { money, parsePriceToCents, parseNumber, number } from 'shared/utils/format'

// Utils
import isEmpty from 'lodash/isEmpty'
import trim from 'lodash/trim'

// UI
import BInput from 'ui/blocks/Input'
import Span from 'ui/elements/Span'

const renderValue = (value, cents) => {
  if (value == null) return ''

  return cents ? money(value) : value
}

const PriceInput = ({
  id,
  currency,
  cents,
  hidePrefix,
  field,
  size = 'md',
  className,
  error,
  inputRef,
  disabled,
  maxValue = null,
  ...otherProps
}) => {
  const inputElement = useRef(null)
  const [displayValue, setDisplayValue] = useState(undefined)

  const resolvedCurrency = currency || accounting.settings.currency.symbol

  const hasMaxValue = maxValue !== null
  const suffixValue = hasMaxValue && renderValue(maxValue, cents)

  const getValue = () => {
    if (displayValue != null) return displayValue

    if (field.value == null) return ''

    if (cents) {
      return money(field.value, { symbol: '' })
    } else {
      return field.value
    }
  }

  const handleChange = (event) => {
    const value = event.target.value

    if (isEmpty(trim(value))) {
      field.onChange('')
      setDisplayValue('')
    } else if (cents) {
      const valueInCents = parsePriceToCents(value)
      field.onChange(valueInCents)
      setDisplayValue(value)
    } else {
      field.onChange(value)
      setDisplayValue(value)
    }
  }

  const handleBlur = (event) => {
    if (field.onBlur) {
      field.onBlur(event)
    }

    const currentValue = event.target.value

    if (isEmpty(trim(currentValue))) {
      return
    }

    if (cents) {
      const valueInCents = parsePriceToCents(currentValue)
      const formattedValue = money(valueInCents, { symbol: '' })
      setDisplayValue(formattedValue)
    } else {
      const numericValue = parseNumber(currentValue)
      field.onChange(numericValue)
      setDisplayValue(number(numericValue))
    }
  }

  const Prefix = () => {
    return (
      <BInput.Prefix modifiers="padding-x-2">
        <Span modifiers="font-size-md text-Text/Primary font-weight-semibold">{resolvedCurrency}</Span>
      </BInput.Prefix>
    )
  }

  const Suffix = () => {
    return (
      <BInput.Suffix modifiers="padding-x-3">
        <Span modifiers="font-size-md text-Text/Tertiary">/ {suffixValue}</Span>
      </BInput.Suffix>
    )
  }

  return (
    <BInput size={size} hasSuffix={hasMaxValue}>
      {resolvedCurrency && !hidePrefix && <Prefix />}
      <BInput.Input
        id={id}
        ref={(ref) => {
          inputElement.current = ref
          if (inputRef) inputRef(ref)
        }}
        {...field}
        type="text"
        onChange={handleChange}
        onBlur={handleBlur}
        value={getValue()}
        size={size}
        error={error}
        className={className}
        disabled={disabled}
        {...otherProps}
      />
      {resolvedCurrency && hasMaxValue && <Suffix />}
    </BInput>
  )
}

PriceInput.propTypes = {
  id: PropTypes.string,
  currency: PropTypes.string,
  cents: PropTypes.bool,
  hidePrefix: PropTypes.bool,
  field: PropTypes.object.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  error: PropTypes.any,
  inputRef: PropTypes.func,
  disabled: PropTypes.bool,
  maxValue: PropTypes.number
}

export default PriceInput
